import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getSubscriptionHealth,
  isSubscriptionInterrupted,
} from '@/features/billing/lib/subscriptionHealth';
import {
  OrganizationRole,
  OrganizationType,
  SubscriptionPlan,
  SubscriptionSource,
  type Organization,
} from '@/types/gql.generated';
import { useToast, Center, Flex, Spinner, Text } from '@/ui';
import { PanelSection } from '../../../components/PanelSection';
import { useOrganizationInvites } from '../hooks/useOrganizationInvites';
import {
  useOrganizationMembers,
  type OrgMember,
} from '../hooks/useOrganizationMembers';
import { useRemoveOrganizationMember } from '../hooks/useRemoveOrganizationMember';
import { useResendOrganizationInvite } from '../hooks/useResendOrganizationInvite';
import { useRevokeOrganizationInvite } from '../hooks/useRevokeOrganizationInvite';
import { getSeatUsage } from '../lib/seatLimit';
import { EditMemberModal } from './EditMemberModal';
import { InviteSection } from './InviteSection';
import { MemberOrganizationPanel } from './MemberOrganizationPanel';
import { MembersList } from './MembersList';
import { OrganizationPlanSection } from './OrganizationPlanSection';
import { PendingInvitesList } from './PendingInvitesList';
import { SeatsSection } from './SeatsSection';

type Props = {
  organization: Organization;
};

export const OrganizationPanel = ({ organization }: Props) => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'organizations',
  });
  const isOwner = organization.role === OrganizationRole.Owner;
  const isFamily = organization.type === OrganizationType.Family;
  const { subscriptionPlan } = organization;
  const health = getSubscriptionHealth(organization);
  const isPlanInactive = isSubscriptionInterrupted(health);
  const {
    members,
    isLoading: isMembersLoading,
    isError: isMembersError,
  } = useOrganizationMembers(organization.id, isOwner);
  const {
    invites,
    isLoading: isInvitesLoading,
    isError: isInvitesError,
  } = useOrganizationInvites(organization.id, isOwner);
  // Gate the roster/invite UI until BOTH queries resolve — the seat math and
  // duplicate-email filtering below read the loaded invite list, so acting on
  // the empty placeholder would undercount seats and miss duplicates.
  const isLoading = isMembersLoading || isInvitesLoading;
  const isError = isMembersError || isInvitesError;
  const { removeMember, isPending: isRemoving } = useRemoveOrganizationMember(
    organization.id
  );
  const { revokeInvite, isPending: isRevoking } = useRevokeOrganizationInvite(
    organization.id
  );
  const { resendInvite, isPending: isResending } = useResendOrganizationInvite(
    organization.id
  );
  const toast = useToast();

  const [editingMember, setEditingMember] = useState<OrgMember | null>(null);

  // Seat-pack management only applies to a Stripe-funded business plan —
  // manual (offline) grants have no subscription to change.
  const seatPacksEnabled =
    organization.type === OrganizationType.Business &&
    subscriptionPlan === SubscriptionPlan.Business &&
    organization.subscriptionSource === SubscriptionSource.Stripe;

  // A scheduled seat-pack decrease already reserves the lower limit — hold
  // invites to it so freed seats can't be refilled before renewal (the server
  // enforces the same rule).
  const effectiveSeatLimit =
    organization.seatLimit != null && organization.pendingSeatLimit != null
      ? Math.min(organization.seatLimit, organization.pendingSeatLimit)
      : (organization.pendingSeatLimit ?? organization.seatLimit);

  if (!isOwner) {
    return (
      <Flex direction="column">
        <MemberOrganizationPanel organization={organization} />
      </Flex>
    );
  }

  // A seat is occupied by an accepted member (memberCount) OR a live pending
  // invite, so both count toward the limit. The roster query drives the invite
  // form, so we only render that form — and the lists — once the roster has
  // actually loaded, otherwise the seat math and duplicate-email filtering would
  // run against an empty placeholder list.
  const usedSeats = organization.memberCount + invites.length;
  const pendingInviteEmails = invites.map((invite) =>
    invite.email.toLowerCase()
  );
  // The invite and seat-pack gates mirror the server's count exactly: accepted
  // members + live (non-expired) pending invites. An expired invite no longer
  // reserves a seat there, so holding one back here would refuse an invite the
  // server would accept — even though the summary below still lists it.
  const occupiedSeats =
    organization.memberCount +
    invites.filter((invite) => !invite.isExpired).length;
  const { remaining, isUnlimited } = getSeatUsage(
    effectiveSeatLimit,
    occupiedSeats
  );

  const seatSummary = isUnlimited
    ? t('members_count', { count: organization.memberCount })
    : t(isFamily ? 'workspace.accounts_used' : 'workspace.seats_used', {
        used: usedSeats,
        limit: effectiveSeatLimit ?? 0,
      });

  const handleRemove = (
    userId: string,
    options?: { onSuccess?: () => void }
  ) => {
    removeMember(userId, {
      onError: () => toast.error(t('member.remove_error')),
      onSuccess: options?.onSuccess,
    });
  };

  const handleRevokeInvite = (
    inviteId: string,
    options?: { onSuccess?: () => void }
  ) => {
    revokeInvite(inviteId, {
      onError: () => toast.error(t('pending_invite.revoke_error')),
      onSuccess: options?.onSuccess,
    });
  };

  const handleResendInvite = (inviteId: string) => {
    resendInvite(inviteId, {
      onError: () => toast.error(t('pending_invite.resend_error')),
      onSuccess: () => toast.notify(t('pending_invite.resend_success')),
    });
  };

  const inviteDescription = isUnlimited
    ? undefined
    : isFamily
      ? t('invite.spaces_available', { count: remaining })
      : t('invite.seats_available');

  return (
    <Flex direction="column">
      {subscriptionPlan && (
        <OrganizationPlanSection
          canManage
          health={health}
          plan={subscriptionPlan}
          seatPacks={organization.seatPacks}
          usage={!isLoading && !isError ? seatSummary : undefined}
        />
      )}

      {isLoading ? (
        <Center py="6">
          <Spinner />
        </Center>
      ) : isError ? (
        <Text color="customgray.mid" mt="6">
          {t('members_error')}
        </Text>
      ) : (
        <>
          {seatPacksEnabled && (
            <PanelSection
              description={t('seats.description')}
              first={!subscriptionPlan}
              title={t('seats.heading')}
            >
              <SeatsSection
                occupiedSeats={occupiedSeats}
                organization={organization}
              />
            </PanelSection>
          )}

          {/* A family leads with the invite call to action; a business folds it
              into the roster, where seat capacity is the real constraint. */}
          {isFamily && (
            <PanelSection
              description={inviteDescription}
              first={!subscriptionPlan}
              title={t('invite.heading_family')}
            >
              <InviteSection
                buttonLabel={t('invite.button_family')}
                collectsContactDetails
                isFamily
                isPlanInactive={isPlanInactive}
                isPrimary
                members={members}
                occupiedSeats={occupiedSeats}
                organizationId={organization.id}
                pendingInviteEmails={pendingInviteEmails}
                seatLimit={effectiveSeatLimit}
              />
            </PanelSection>
          )}

          {invites.length > 0 && (
            <PanelSection
              first={!subscriptionPlan && !seatPacksEnabled && !isFamily}
              title={t('invites_section')}
            >
              <PendingInvitesList
                invites={invites}
                isResending={isResending}
                isRevoking={isRevoking}
                smsContactsEnabled={isFamily}
                onResend={handleResendInvite}
                onRevoke={handleRevokeInvite}
              />
            </PanelSection>
          )}

          <PanelSection
            description={isFamily ? undefined : inviteDescription}
            title={t('members_section')}
            first={
              !subscriptionPlan &&
              !seatPacksEnabled &&
              !isFamily &&
              invites.length === 0
            }
          >
            {!isFamily && (
              <InviteSection
                buttonLabel={t('invite.button_business')}
                isPlanInactive={isPlanInactive}
                members={members}
                occupiedSeats={occupiedSeats}
                organizationId={organization.id}
                pendingInviteEmails={pendingInviteEmails}
                seatLimit={effectiveSeatLimit}
              />
            )}

            <MembersList
              isRemoving={isRemoving}
              members={members}
              organizationName={organization.name}
              smsContactsEnabled={isFamily}
              onEditContact={setEditingMember}
              onRemove={handleRemove}
            />
          </PanelSection>

          {editingMember && (
            <EditMemberModal
              isOpen
              member={editingMember}
              organizationId={organization.id}
              onClose={() => setEditingMember(null)}
            />
          )}
        </>
      )}
    </Flex>
  );
};
